/**
 * Add Event modal — records a real-world event against an event-based
 * compliance occurrence, giving it a due date so it joins the list +
 * calendar views.
 *
 * Flow: pick fund → pick an event-based compliance awaiting its event →
 * enter event name/date → due date auto-suggested as event date + the
 * item's "days after" rule (editable) → save.
 */
import React, { useEffect, useState } from 'react';
import { X, Loader, CalendarPlus } from 'lucide-react';
import { complianceAPI } from '../../services/api';

const AddEventModal = ({ open, onClose, funds = [], defaultFundId = '', onSaved }) => {
  const [fundId, setFundId] = useState(defaultFundId);
  const [pending, setPending] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [selected, setSelected] = useState(null);
  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [dueTouched, setDueTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setFundId(defaultFundId || '');
      setSelected(null);
      setEventName('');
      setEventDate('');
      setDueDate('');
      setDueTouched(false);
      setError(null);
    }
  }, [open, defaultFundId]);

  useEffect(() => {
    if (!open || !fundId) { setPending([]); return; }
    setLoadingPending(true);
    setSelected(null);
    complianceAPI.listPendingEvents(fundId)
      .then(r => setPending(r.data || []))
      .catch(() => setPending([]))
      .finally(() => setLoadingPending(false));
  }, [open, fundId]);

  // Auto-suggest due date = event date + event_due_days_after
  useEffect(() => {
    if (dueTouched || !eventDate || !selected) return;
    const days = selected.event_due_days_after;
    if (days == null) return;
    const d = new Date(eventDate);
    if (isNaN(d.getTime())) return;
    d.setDate(d.getDate() + Number(days));
    setDueDate(d.toISOString().slice(0, 10));
  }, [eventDate, selected, dueTouched]);

  if (!open) return null;

  const handleSave = async () => {
    setError(null);
    if (!selected) { setError('Pick the compliance whose event you are recording.'); return; }
    if (!eventName.trim()) { setError('Event name is required.'); return; }
    if (!dueDate) { setError('Due date is required.'); return; }
    setSaving(true);
    try {
      await complianceAPI.updateOccurrence(selected.occurrence_id, {
        due_date: dueDate,
        event_name: eventName.trim(),
        wip_status: 'Active',
      });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add event');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="add-event-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900 inline-flex items-center gap-2">
            <CalendarPlus size={18} className="text-blue-600" /> Add Event
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded px-3 py-2" data-testid="add-event-error">{error}</div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Fund</label>
            <select value={fundId} onChange={e => setFundId(e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm" data-testid="add-event-fund">
              <option value="">Choose fund…</option>
              {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>

          {fundId && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Event-based compliances assigned to this fund
              </label>
              {loadingPending ? (
                <div className="text-sm text-gray-500 py-3 inline-flex items-center gap-2"><Loader size={14} className="animate-spin" /> Loading…</div>
              ) : pending.length === 0 ? (
                <div className="text-sm text-gray-500 py-3 italic border rounded-lg px-3 bg-gray-50" data-testid="add-event-empty">
                  No event-based compliances are pending an event for this fund.
                </div>
              ) : (
                <div className="border rounded-lg divide-y max-h-48 overflow-y-auto" data-testid="add-event-list">
                  {pending.map(p => (
                    <label key={p.occurrence_id}
                           className={`flex items-start gap-3 px-3 py-2.5 cursor-pointer hover:bg-blue-50 ${selected?.occurrence_id === p.occurrence_id ? 'bg-blue-100' : ''}`}
                           data-testid={`add-event-option-${p.occurrence_id}`}>
                      <input type="radio" name="pending-event" className="mt-1"
                             checked={selected?.occurrence_id === p.occurrence_id}
                             onChange={() => setSelected(p)} />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{p.item_name}</div>
                        <div className="text-xs text-gray-500">
                          {p.section_name}{p.event_trigger ? ` · Trigger: ${p.event_trigger}` : ''}
                          {p.event_due_days_after != null ? ` · Due ${p.event_due_days_after} day(s) after event` : ''}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {selected && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50/60 border border-blue-100 rounded-lg p-4">
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Event Name</label>
                <input type="text" value={eventName} onChange={e => setEventName(e.target.value)}
                       placeholder={selected.event_trigger ? `e.g. ${selected.event_trigger}` : 'e.g. AGM held on 20-Sep'}
                       className="w-full border rounded px-3 py-2 text-sm"
                       data-testid="add-event-name" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Event Date</label>
                <input type="date" value={eventDate} onChange={e => setEventDate(e.target.value)}
                       className="w-full border rounded px-3 py-2 text-sm"
                       data-testid="add-event-date" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Due Date {selected.event_due_days_after != null && <span className="text-gray-500">(auto = event + {selected.event_due_days_after}d)</span>}
                </label>
                <input type="date" value={dueDate}
                       onChange={e => { setDueTouched(true); setDueDate(e.target.value); }}
                       className="w-full border rounded px-3 py-2 text-sm"
                       data-testid="add-event-due-date" />
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 border rounded text-sm" data-testid="add-event-cancel">Cancel</button>
          <button onClick={handleSave} disabled={saving || !selected}
                  className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="add-event-save">
            {saving && <Loader size={14} className="animate-spin" />}
            Add Event
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEventModal;
